import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  BackButton,
  BehaviourDropDown,
  DropDown,
  PeriodDropDown,
  Loader,
  Timeline,
  Icon,
} from "@src/components/atoms";
import {
  BehaviourDescriptionSection,
  SectionContainer,
} from "@src/pages/sharedSections";
import { default as TipsCard } from "./TipsCard";
import { GoalResultCard } from "@src/components/cards/operator";
import moment from "moment";
import { GoalHistoricalPerformanceCard } from "@src/components/cards/operator";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { useDCContext } from "@src/context/dcContext";
import { useOperatorContext } from "@src/context/operatorContext";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import AppUtils from "@src/utils/appUtils";
import { useNavigate } from "react-router-dom";
import { useIsMobileWidth } from "@src/utils/mediaQuery";
import { BehaviourDropDownOperator } from "../../../components/atoms/BehaviourDropDown";
import { checkShouldCustomiseGoal } from "@src/components/cards/cardHelpers";
import ErrorPage from "@src/components/layout/ErrorPage";

const TIMELINE_DIG_DEEPER_SHOWN_KEY = "timeline_dig_deeper_shown";

const GoalsPage = () => {
  const dc = useDCContext();
  const { getDynamicReplacement, countSimilarDynamicReplacements } = dc;
  const {
    profile,
    setProfile,
    selectedPeriod,
    setSelectedPeriod,
    selectedBehaviour,
    setSelectedBehaviour,
    setSelectedEvent,
    getLatestVehicle,
    periodName,
  } = useOperatorContext();
  const navigate = useNavigate();
  const { customer, behaviours, operator, team } = profile;
  // const [selectedGoal, setSelectedGoal] = useState(null);

  const [digDeeperDismissed, setDigDeeperDismissed] = useState(
    () =>
      !!profile.operatorMetadata?.some(
        (m) => m.key === TIMELINE_DIG_DEEPER_SHOWN_KEY,
      ),
  );
  const digDeeperMutation = OperatorHooks.useSubmitOperatorProfile(
    (profileData) => profileData && setProfile(profileData),
  );
  const dismissDigDeeper = () => {
    setDigDeeperDismissed(true);
    digDeeperMutation.mutateAsync({
      metadata: [
        {
          key: TIMELINE_DIG_DEEPER_SHOWN_KEY,
          type: "STRING",
          value: "true",
        },
      ],
    });
  };

  const industryFormat =
    customer?.industry === "shipping" ? "YYYY-MM-DD" : "YYYY-MM";

  const isMobile = useIsMobileWidth();
  //testing
  const shippingPeriodRange = AppUtils.getDateRangeFromPeriodKey(
    moment.utc(selectedPeriod?.groupKey || "").format(industryFormat),
  );
  const aviationPeriodRange = AppUtils.getDateRangeFromPeriodKey(
    moment.utc(selectedPeriod?.groupKey || "").format(industryFormat),
  );

  const selected =
    customer?.industry === "shipping"
      ? shippingPeriodRange
      : aviationPeriodRange;

  //Goals
  const {
    data: { goalsData, currentPeriod } = {},
    isPending: goalsPending,
    error: goalsError,
  } = OperatorHooks.useOperatorGoals(profile?.operator?.id, customer?.industry);
  //All events
  const {
    data: { events } = {},
    isPending: eventsPending,
    error: eventsError,
  } = OperatorHooks.useOperatorEvents(
    selected?.from,
    selected?.to,
    profile?.operator?.id,
    customer?.industry,
  );
  //Baselines
  const {
    data: { baselines } = {},
    isPending: baselinesPending,
    error: baselinesError,
  } = OperatorHooks.useOperatorBaselines(
    profile?.operator?.id,
    customer?.industry,
  );

  const { data: { exclusions } = {} } = OperatorHooks.useBehaviourExclusions(
    profile?.operator?.id,
  );

  const options = behaviours?.map((b) => {
    return { ...b, value: b.behaviourAcronym, text: b.behaviourName };
  });

  useEffect(() => {
    if (!selectedBehaviour) {
      setSelectedBehaviour(options[0]);
    }
  }, [selectedBehaviour]);

  useEffect(() => {
    if (!!currentPeriod && !selectedPeriod) {
      setSelectedPeriod(currentPeriod);
    }
  }, [currentPeriod]);

  if (
    (!!eventsPending || !!baselinesPending) &&
    (!selectedBehaviour || !selectedPeriod)
  )
    return (
      <div className="min-h-120">
        <div className="w-full h-full flex items-center justify-center">
          <Loader size="2xl" />
        </div>
      </div>
    );

  const selectedGoal =
    !!goalsData &&
    goalsData
      ?.find(
        (g) =>
          g.groupKey === selectedPeriod?.groupKey ||
          g.groupKey === selectedPeriod?.value,
      )
      ?.behaviours?.find(
        (b) => b.behaviourAcronym === selectedBehaviour?.behaviourAcronym,
      );

  const selectedTarget = team.targetBehaviours.find(
    (a) => a.acronym == selectedBehaviour?.behaviourAcronym,
  );
  const goalColour = getBehaviourColourForPosition(selectedBehaviour.order);

  return (
    <section>
      {!!goalsPending || !!baselinesPending ? (
        <div className="min-h-120">
          <div className="w-full h-full flex items-center justify-center">
            <Loader size="2xl" />
          </div>
        </div>
      ) : !!goalsError ? (
        <ErrorPage pageName={"goals"} />
      ) : !!baselinesError ? (
        <div>Error fetching baselines...</div>
      ) : !!goalsData && goalsData.length > 0 ? (
        <div className="px-2 md:px-4">
          <div className="flex flex-col md:flex-row mx-4 my-4 items-center relative">
            <div
              className={
                isMobile ? `w-full m-2` : `absolute md:static self-start`
              }
            >
              <BackButton trackingActionKey="TARGETS_BACK_BUTTON" />
            </div>
            <h1
              className={`w-full text-2xl md:text-4xl m-0 h-14 md:h-auto text-center ${goalColour?.name && `text-${goalColour.name}-500`}`}
            >
              {selectedBehaviour.behaviourName}
            </h1>
          </div>
          <div className="flex flex-col md:flex-row flex-wrap gap-4 mx-4 my-4 items-center justify-center relative">
            {!!goalsData && goalsData.length > 0 && (
              <div className="w-[300px] md:w-1/3 flex justify-center items-stretch">
                <BehaviourDropDownOperator
                  behaviours={
                    goalsData?.find(
                      (g) => g.groupKey === selectedPeriod?.groupKey,
                    )?.behaviours || []
                  }
                  minWidth={false}
                  fullWidth={true}
                />
              </div>
            )}
            {!!goalsData && goalsData.length > 0 && (
              <div className="w-[300px] md:w-1/3 flex justify-center items-stretch">
                <PeriodDropDown
                  goalsData={goalsData ? goalsData : []}
                  minWidth={false}
                  fullWidth={true}
                />
              </div>
            )}
          </div>

          {!!selectedGoal && (
            <div className="flex flex-col gap-4">
              <GoalResultCard
                behaviourAcronym={selectedGoal.behaviourAcronym}
                targetGoal={selectedGoal.goalTargetEvents}
                targetActual={Math.max(
                  selectedGoal.succeeded,
                  selectedGoal.succeededIncludingSelfReported,
                )}
                targetAchieved={selectedGoal.goalAchieved}
                preferredEventName={customer.preferredEventName}
                preferredEventNamePlural={customer.preferredEventNamePlural}
                customiseGoalReason={checkShouldCustomiseGoal(
                  selectedGoal,
                  goalsData?.find(
                    (g) => g.groupKey === selectedPeriod?.groupKey,
                  )?.behaviours || [],
                )}
                industry={customer?.industry}
                position={selectedGoal.order}
                isOpen={selectedGoal.isOpenPeriod}
                isCurrent={
                  selectedPeriod?.value === currentPeriod.groupKey ||
                  selectedPeriod?.groupKey === currentPeriod.groupKey
                }
                periodName={periodName}
                baselines={baselines}
                recentAverageType={selectedGoal.recentAverageType}
                baselineDateSource={selectedTarget?.baselineDateSource}
              />
              {eventsPending ? (
                <div className="min-h-120">
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader size="2xl" />
                  </div>
                </div>
              ) : eventsError ? (
                <div>Error fetching events...</div>
              ) : (
                !!events && (
                  <SectionContainer paddingY={2} shadow={false}>
                    <Timeline
                      events={events}
                      periodKey={selected?.groupKey || selected?.value}
                      pRange={
                        customer?.industry === "shipping"
                          ? shippingPeriodRange
                          : aviationPeriodRange
                      }
                      target={selectedGoal}
                      getDynamicContent={getDynamicReplacement}
                      industry={customer?.industry}
                      customerCode={customer.customerCode}
                      periodName={periodName}
                      preferredEventName={customer.preferredEventName}
                      digDeeperDismissed={digDeeperDismissed}
                      onDismissDigDeeper={dismissDigDeeper}
                      handleBubbleClick={(ev) => {
                        setSelectedEvent(ev);
                        navigate(`/dashboard/operator/events/${ev.id}`);
                      }}
                    />
                  </SectionContainer>
                )
              )}
              {!AppUtils.isFeatureAvailableForBehaviour(
                "BehaviourWithNoFuelSavings",
                selectedBehaviour?.behaviourAcronym,
              ) && (
                <GoalHistoricalPerformanceCard
                  target={selectedGoal}
                  myTargets={goalsData}
                  behaviour={selectedBehaviour}
                  industry={customer?.industry}
                  firstEnrolledDate={moment
                    .utc(
                      operator?.firstEnrolledPeriodStart ||
                        operator?.enrolledDate,
                    )
                    .startOf("day")}
                />
              )}
              <TipsCard
                behaviourAcronym={selectedBehaviour?.behaviourAcronym}
                currentVehicle={getLatestVehicle()}
              />
              <SectionContainer paddingY={2} shadow={false}>
                <BehaviourDescriptionSection
                  target={selectedGoal}
                  behaviours={behaviours}
                  customer={customer}
                  exclusions={exclusions}
                  currentVehicle={getLatestVehicle()}
                />
              </SectionContainer>
            </div>
          )}
        </div>
      ) : !!goalsData ? (
        <div className="flex gap-2 items-center justify-center text-left p-6">
          <Icon name={"clockwise"} inline /> Signol is awaiting data from your
          company.
        </div>
      ) : null}
    </section>
  );
};

export const goalsPageControls = {};

GoalsPage.propTypes = AppUtils.getPropTypeFromControls(goalsPageControls);

export default GoalsPage;
